/**
 * Which approved material this article is written from.
 *
 * The named choices - persona, treatment, supervisor, CTA - are single picks and
 * have always been per article. Everything else went wholesale: every strength,
 * every policy and every piece of equipment approved for the store, on every
 * generation, whether the article was about them or not. A clinic with twenty
 * registered strengths writing about one treatment handed the model nineteen it
 * did not need - and the model used them.
 *
 * Two states per group, because that is the actual decision:
 *
 *   すべて  the default, and what every article did before this existed. Stored
 *           as null, which the generator reads as "everything in scope" - so a
 *           strength approved next week is included without anybody revisiting
 *           the article.
 *
 *   選択    exactly these. An empty selection is a real instruction ("none of
 *           them"), not an absence, which is why the two states are explicit
 *           rather than inferred from whether anything is ticked.
 *
 * No paragraph explaining that on screen: a control that needs a paragraph is
 * the wrong control. The switch says すべて or 選択, and the chips say what is in.
 */
import type { CatalogItem } from '../api.ts';
import { Pill } from './ui.tsx';

/** The categories a strength row can carry, in the order the catalog lists them. */
const CATEGORY_LABEL: Record<string, string> = {
  STRENGTH: '強み',
  TREATMENT_POLICY: '施術方針',
  COUNSELING_POLICY: 'カウンセリング方針',
  EQUIPMENT: '設備・機器',
  OTHER: 'その他の情報',
};

const CATEGORY_ORDER = [
  'STRENGTH',
  'TREATMENT_POLICY',
  'COUNSELING_POLICY',
  'EQUIPMENT',
  'OTHER',
];

/** Catalog rows carry open-ended fields; only strings are safe to render. */
const asText = (value: unknown): string => (typeof value === 'string' ? value : '');

export interface MaterialScopeValue {
  /**
   * Per category: a missing key is "all of that category", an array names
   * exactly what is wanted, and `[]` is a deliberate none.
   *
   * Per category and not one list, because the categories are independent
   * decisions. One shared array made them one: picking a single 施術方針 turned
   * it non-null, and 強み・カウンセリング方針・設備 all switched from "send
   * everything" to "send exactly these" without anybody touching them.
   */
  strengthScope: Record<string, string[] | null>;
  supervisorIds: string[] | null;
}

function Group({
  title,
  items,
  selected,
  onChange,
  label,
}: {
  title: string;
  items: CatalogItem[];
  /** null is "all of them". */
  selected: string[] | null;
  onChange: (next: string[] | null) => void;
  label: (item: CatalogItem) => string;
}) {
  if (items.length === 0) return null;
  const all = selected === null;

  return (
    <div className="scopepick">
      <div className="scopepick__head">
        <span className="scopepick__title">
          {title}
          <span className="muted small"> {items.length}件</span>
        </span>
        {/*
          One control, two states. A checkbox per row plus a "select all" would
          be the same thing said twice, and would leave "none ticked" meaning
          two different things depending on which one you last pressed.
        */}
        <div className="chips">
          <button
            type="button"
            className="chip"
            aria-pressed={all}
            onClick={() => onChange(null)}
          >
            すべて渡す
          </button>
          <button
            type="button"
            className="chip"
            aria-pressed={!all}
            onClick={() => onChange(selected ?? items.map((item) => item.id))}
          >
            選ぶ
          </button>
          {selected !== null && (
            <Pill tone={selected.length === 0 ? 'warn' : 'muted'}>
              {selected.length === 0 ? '渡さない' : `${selected.length}件`}
            </Pill>
          )}
        </div>
      </div>

      <div className="chips scopepick__items">
        {items.map((item) => {
          const on = all || (selected?.includes(item.id) ?? false);
          return (
            <button
              key={item.id}
              type="button"
              className="chip"
              aria-pressed={on}
              // While the group is on すべて, pressing an item is how somebody
              // says "just this one" - it switches the group and starts the
              // selection from what they pressed.
              onClick={() =>
                onChange(
                  selected === null
                    ? [item.id]
                    : on
                      ? selected.filter((id) => id !== item.id)
                      : [...selected, item.id],
                )
              }
            >
              {label(item)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function MaterialScope({
  strengths,
  supervisors,
  value,
  onChange,
}: {
  strengths: CatalogItem[];
  supervisors: CatalogItem[];
  value: MaterialScopeValue;
  onChange: (next: MaterialScopeValue) => void;
}) {
  const byCategory = CATEGORY_ORDER.map((category) => ({
    category,
    items: strengths.filter((item) => asText(item.category) === category),
  })).filter((group) => group.items.length > 0);

  if (byCategory.length === 0 && supervisors.length === 0) return null;

  return (
    <div className="scopepicks">
      {byCategory.map((group) => (
        <Group
          key={group.category}
          title={CATEGORY_LABEL[group.category] ?? group.category}
          items={group.items}
          /*
           * This category's own answer, and nothing else's. A key that is not
           * there means "all of this category" - narrowing 施術方針 leaves 強み
           * exactly as it was.
           */
          selected={value.strengthScope[group.category] ?? null}
          onChange={(next) => {
            const scope = { ...value.strengthScope };
            // Back to "all" is the ABSENCE of a key, not a null stored under it:
            // absence keeps including material approved later.
            if (next === null) delete scope[group.category];
            else scope[group.category] = next;
            onChange({ ...value, strengthScope: scope });
          }}
          label={(item) => asText(item.title) || '（名称なし）'}
        />
      ))}

      {supervisors.length > 0 && (
        <Group
          title="監修医"
          items={supervisors}
          selected={value.supervisorIds}
          onChange={(next) => onChange({ ...value, supervisorIds: next })}
          label={(item) => asText(item.name) || '（名称なし）'}
        />
      )}
    </div>
  );
}
